import React from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { VideoSource } from "../../Enums/enums";
import { hasReviewedEdit } from "../batchEditor/batchItems";
import { feedLabel, feedPartnerPlatforms, selectedPartnerFeeds } from "../partnerFeeds";

const SOURCE_LABEL = {
  [VideoSource.ORIGINAL]: "Original",
  [VideoSource.LOCAL]: "Local upload",
  [VideoSource.EDITED]: "Video Editor edit",
  [VideoSource.DUPLICATE]: "Duplicate",
};

const SOURCE_COLOR = {
  [VideoSource.ORIGINAL]: "default",
  [VideoSource.LOCAL]: "info",
  [VideoSource.EDITED]: "primary",
  [VideoSource.DUPLICATE]: "warning",
};

const focusSx = {
  "&.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: -2,
  },
};

function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Box sx={{ mt: 0.25, minWidth: 0, overflowWrap: "anywhere" }}>{children}</Box>
    </Box>
  );
}

function PlatformCard({ platform, cfg, rephrased }) {
  const src = cfg.source || VideoSource.ORIGINAL;
  let detail = null;
  if (src === VideoSource.DUPLICATE && cfg.duplicateOf)
    detail = <Typography variant="body2">{`Reusing ${cfg.duplicateOf} URL`}</Typography>;
  else if (src === VideoSource.EDITED && hasReviewedEdit(cfg))
    detail = <Chip icon={<CheckCircleIcon />} label="Payload ready" color="success" size="small" />;
  else if (src === VideoSource.LOCAL && cfg.localVideoPath)
    detail = <Typography variant="body2">{cfg.localVideoPath}</Typography>;

  const vertical =
    cfg.convertToVertical && (src === VideoSource.ORIGINAL || src === VideoSource.LOCAL);

  const thumbSource = cfg.thumbnailSource || "none";
  const thumb =
    thumbSource === "custom" && cfg.thumbnailPreview ? (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box
          component="img"
          src={cfg.thumbnailPreview}
          alt="Custom thumbnail preview"
          sx={{ height: 28, maxWidth: 60, objectFit: "cover", borderRadius: 0.5, border: "1px solid", borderColor: "divider" }}
        />
        <Chip label="Custom" color="success" size="small" />
      </Stack>
    ) : thumbSource === "none" ? (
      <Chip label="None" size="small" color="default" variant="outlined" />
    ) : (
      <Chip label="Original" size="small" variant="outlined" />
    );

  const title = rephrased?.[platform]?.title;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip label={platform} size="small" color="primary" />
        <Chip label={SOURCE_LABEL[src] || src} color={SOURCE_COLOR[src]} size="small" />
        {vertical && <Chip label="9:16 vertical" size="small" color="secondary" />}
        <Chip
          label={title ? "Title filled" : "Title missing"}
          size="small"
          color={title ? "success" : "warning"}
          variant="outlined"
        />
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
        }}
      >
        <Field label="Rephrased title">
          {title ? (
            <Typography variant="body2">{title}</Typography>
          ) : (
            <Chip label="None" size="small" color="warning" variant="outlined" />
          )}
        </Field>
        <Field label="Detail">{detail || <Typography variant="body2" color="text.secondary">—</Typography>}</Field>
        <Field label="Thumbnail">{thumb}</Field>
      </Box>
    </Paper>
  );
}

function VideoRow({ video, selectedPlatforms, videoConfigs, videoRephrased }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1, minWidth: 0 }}>
        <VideoLibraryIcon fontSize="small" color="action" sx={{ mt: 0.25 }} aria-hidden />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
            {video.title || video.video_title || video.unique_key}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            ({video.unique_key})
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        {selectedPlatforms.map((platform) => (
          <PlatformCard
            key={platform}
            platform={platform}
            cfg={videoConfigs[platform] || {}}
            rephrased={videoRephrased}
          />
        ))}
      </Box>
    </Box>
  );
}

function Stat({ value, label }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: "left", sm: "center" } }}>
      <Typography variant="h6" fontWeight={600} component="div">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Step4Review({ selectedBrands, brandVideos, brandPlatforms, platformConfigs, brandRephrasedContent, selectedFeeds, brandFeedsInfo, partnerFeedPlatforms }) {
  const totalVideos = Object.values(brandVideos || {}).reduce((s, v) => s + v.length, 0);
  const totalJobs = (selectedBrands || []).reduce((sum, b) => {
    const platforms = (brandPlatforms || {})[b.name] || [];
    return sum + ((brandVideos || {})[b.name] || []).length * platforms.length;
  }, 0);
  const brandCount = (selectedBrands || []).length;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step 5 — Review & Publish
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 2, borderRadius: 2 }} role="status">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          What will happen when you publish
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }}>
          <Stat value={brandCount} label={`brand${brandCount !== 1 ? "s" : ""}`} />
          <Stat value={totalVideos} label={`video${totalVideos !== 1 ? "s" : ""}`} />
          <Stat value={totalJobs} label={`publish job${totalJobs !== 1 ? "s" : ""} will be queued`} />
        </Stack>
      </Paper>

      {brandCount === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No brands selected — nothing to review.
        </Alert>
      )}

      {(selectedBrands || []).map((brand) => {
        const videos = (brandVideos || {})[brand.name] || [];
        return (
          <Accordion
            key={brand.name}
            defaultExpanded
            disableGutters
            variant="outlined"
            sx={{ mb: 1, borderRadius: 2, "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: { xs: 48, sm: 40 }, ...focusSx }}
              aria-label={`${brand.name}, ${videos.length} videos`}
            >
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                <BusinessIcon color="primary" fontSize="small" aria-hidden />
                <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                  {brand.name}
                </Typography>
                <Chip
                  label={`${videos.length} video${videos.length !== 1 ? "s" : ""}`}
                  size="small"
                  color="primary"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              {/* Two or more Partner feeds: which Partner platforms go to which feed. */}
              {(() => {
                const partnerFeeds = selectedPartnerFeeds(brand.name, selectedFeeds, brandFeedsInfo);
                if (partnerFeeds.length < 2) return null;
                return (
                  <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5 }} data-testid="review-partner-feeds">
                    <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Partner feeds
                    </Typography>
                    {partnerFeeds.map((f) => (
                      <Box key={f.id} display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mt={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {feedLabel(f)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        {feedPartnerPlatforms(brand.name, f.id, brandPlatforms, partnerFeedPlatforms).map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" color="warning" />
                        ))}
                      </Box>
                    ))}
                  </Paper>
                );
              })()}
              {videos.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No videos for this brand.
                </Typography>
              )}
              {videos.map((video) => (
                <VideoRow
                  key={video.unique_key}
                  video={video}
                  selectedPlatforms={(brandPlatforms || {})[brand.name] || []}
                  videoConfigs={(platformConfigs || {})[brand.name]?.[video.unique_key] || {}}
                  videoRephrased={(brandRephrasedContent || {})[brand.name]?.[video.unique_key] || {}}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Alert severity="info" sx={{ mt: 2 }}>
        Clicking <strong>Publish</strong> will queue all jobs.
        Edited video renders process in the background before videos are added to feeds.
      </Alert>
    </Box>
  );
}
